
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import UndoIcon from '@mui/icons-material/Undo';
import CloseIcon from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import { useConceptGate } from '../../../context/ConceptGateContext';
import { useFirebase } from '../../../context/FirebaseContext';
import { GateStatus, reorderPipelineRules } from '../../../api/conceptgate';
import { formatResetTime } from '../../../utils/dateUtils';
import { getNudgeMessage } from '../../../constants/validationConstants';
import RuleItem from './RuleItem';
import ValidationStatusBanner from './ValidationStatusBanner';
import PipelineSelector from './PipelineSelector';
import { isCodexUi } from '../../../utils/codexEnv';
import logger from '../../../utils/logger';

const PIPELINE_COLLAPSED_KEY = 'ntb:conceptgate:pipelineCollapsed';



const ValidationPipeline = ({
  gates,
  status,
  validationResult,
  userRules = [],
  libraryRules = [],
  onOpenRulesManagement,
  onValidate,
  onSaveToJournal,
  isJournalSaved = false,
  onCancelValidation,
  validating = false,
  hasTrade = false,

  streamingProgress = { current: 0, total: 0 },
  subscriptionStatus = null,
  isAdmin = false,
  isLoading = false,
  isExpanded,
  onToggle,
}) => {
  const { openConfigModal, removeUserRule, undoQueue, undoRuleRemovalById, dismissUndoItem, toggleUserRuleEnabled, backgroundValidationMap, selectedInstrument, activePipeline, loadUserRules } = useConceptGate();
  const { auth } = useFirebase();

  // Drag and drop state
  const [draggedRuleId, setDraggedRuleId] = useState(null);
  const [dragOverRuleId, setDragOverRuleId] = useState(null);
  const [isReordering, setIsReordering] = useState(false);

  // Handle rule removal - uses context's undo mechanism
  const handleRemoveRule = async (userRule, libraryRule) => {
    await removeUserRule(userRule.id, libraryRule?.name || userRule?.name);
  };

  const handleToggleEnabled = async (userRule) => {
    if (userRule && userRule.id) {
      await toggleUserRuleEnabled(userRule.id, userRule.enabled !== false);
    }
  };

  // Get user rules sorted by execution order (needed for drag-and-drop)
  const sortedUserRules = useMemo(() => {
    return [...userRules].sort((a, b) => {
      const orderA = a.executionOrder || 0;
      const orderB = b.executionOrder || 0;
      return orderA - orderB;
    });
  }, [userRules]);

  // Drag and drop handlers
  const handleDragStart = useCallback((e, ruleId) => {
    const ruleIdStr = String(ruleId);
    setDraggedRuleId(ruleIdStr);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', ruleIdStr);
    // Set drag image to the whole item
    if (e.target) {
      e.dataTransfer.setDragImage(e.target, 20, 20);
    }
  }, []);

  const handleDragOver = useCallback((e, ruleId) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    const ruleIdStr = String(ruleId);
    if (ruleIdStr !== draggedRuleId) {
      setDragOverRuleId(ruleIdStr);
    }
  }, [draggedRuleId]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOverRuleId(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedRuleId(null);
    setDragOverRuleId(null);
  }, []);

  const handleDrop = useCallback(async (e, targetRuleId) => {
    e.preventDefault();
    e.stopPropagation();
    const sourceRuleId = draggedRuleId;
    const targetRuleIdStr = String(targetRuleId);

    if (!sourceRuleId || sourceRuleId === targetRuleIdStr || !activePipeline?.id) {
      handleDragEnd();
      return;
    }

    // Find source and target indices in sorted rules
    const sourceIndex = sortedUserRules.findIndex(r => String(r.id) === sourceRuleId);
    const targetIndex = sortedUserRules.findIndex(r => String(r.id) === targetRuleIdStr);

    if (sourceIndex === -1 || targetIndex === -1) {
      handleDragEnd();
      return;
    }

    // Create new order by moving source to target position
    const newRules = [...sortedUserRules];
    const [movedRule] = newRules.splice(sourceIndex, 1);
    newRules.splice(targetIndex, 0, movedRule);

    // Create rule orders array with new execution orders
    const ruleOrders = newRules.map((rule, index) => ({
      ruleId: rule.id,
      executionOrder: index,
    }));

    setIsReordering(true);
    handleDragEnd();

    try {
      await reorderPipelineRules(auth, (result) => {
        if (result && !result.error) {
          // Refresh rules to get updated order
          if (loadUserRules) {
            loadUserRules();
          }
        } else {
          logger.error('Failed to reorder rules:', result?.error);
        }
        setIsReordering(false);
      }, activePipeline.id, ruleOrders);
    } catch (err) {
      logger.error('Error reordering rules:', err);
      setIsReordering(false);
    }
  }, [draggedRuleId, sortedUserRules, activePipeline, auth, loadUserRules, handleDragEnd]);

  // Check if user can validate based on subscription limits
  const canValidateByLimit = subscriptionStatus?.canValidateStatic !== false;
  const limitReached = subscriptionStatus && !subscriptionStatus.canValidateStatic;

  // Calculate remaining validations for display
  const getRemainingText = () => {
    if (!subscriptionStatus) return null;
    const { staticUsed, staticLimit } = subscriptionStatus;
    if (staticLimit === -1) return null; // Unlimited
    const remaining = Math.max(0, staticLimit - staticUsed);
    if (remaining <= 3 && remaining > 0) {
      return `${remaining} left today`;
    }
    return null;
  };
  const remainingText = getRemainingText();

  // Skeleton loader for initial loading
  const SkeletonRuleItem = () => (
    <div className="rule-item-skeleton">
      <div className="skeleton-icon"></div>
      <div className="skeleton-content">
        <div className="skeleton-title"></div>
        <div className="skeleton-subtitle"></div>
      </div>
    </div>
  );

  // Collapse state - use controlled props if provided (accordion mode), otherwise use internal state
  const [internalCollapsed, setInternalCollapsed] = useState(() => {
    const saved = localStorage.getItem(PIPELINE_COLLAPSED_KEY);
    return saved === 'true';
  });

  // Use controlled state if provided (accordion mode), otherwise use internal state
  const isCollapsed = isExpanded !== undefined ? !isExpanded : internalCollapsed;

  // Persist collapse state only when using internal state
  useEffect(() => {
    if (isExpanded === undefined) {
      localStorage.setItem(PIPELINE_COLLAPSED_KEY, internalCollapsed.toString());
    }
  }, [internalCollapsed, isExpanded]);

  // Create validation results map - try multiple keys for matching
  const validationMap = useMemo(() => {
    const map = new Map();
    if (gates && Array.isArray(gates)) {
      gates.forEach((gate) => {
        // Try different possible keys
        // Primary key: userRuleId (unique per instance)
        if (gate.userRuleId) {
          map.set(gate.userRuleId.toString(), gate);
        }
        // Fallback keys
        if (gate.libraryRuleId) {
          map.set(gate.libraryRuleId.toString(), gate);
        }
        if (gate.rule) {
          map.set(gate.rule.toString(), gate);
        }
        if (gate.ruleName) {
          map.set(gate.ruleName, gate);
        }
      });
    }
    return map;
  }, [gates]);

  const [expandedGates, setExpandedGates] = useState({});

  // Auto-expand failed rules when validation completes
  useEffect(() => {
    if (gates && gates.length > 0 && !validating) {
      const failedGateIds = {};
      gates.forEach(gate => {
        if (gate.status === 'FAILED' || gate.status === GateStatus.FAILED) {
          // Use userRuleId if available, otherwise libraryRuleId or name
          const key = gate.userRuleId || gate.libraryRuleId || gate.ruleName || gate.rule;
          if (key) {
            failedGateIds[key] = true;
          }
        }
      });

      // Only update if there are failed gates
      if (Object.keys(failedGateIds).length > 0) {
        setExpandedGates(prev => ({
          ...prev,
          ...failedGateIds
        }));
      }
    }
  }, [gates, validating]);

  // Calculate estimated time remaining during validation
  const estimatedTimeRemaining = useMemo(() => {
    if (!validating || !streamingProgress || streamingProgress.total === 0) return null;

    const { current, total } = streamingProgress;
    const remaining = total - current;

    // Average ~2 seconds per static rule, ~5 seconds per AI rule
    // Since we don't know the type of remaining rules, use 3s average
    const avgTimePerRule = 3;
    const remainingSeconds = remaining * avgTimePerRule;

    if (remainingSeconds <= 0) return null;
    return Math.ceil(remainingSeconds);
  }, [validating, streamingProgress]);

  // Get current rule being validated for smart nudge
  const currentValidatingRule = useMemo(() => {
    if (!validating || !streamingProgress || streamingProgress.total === 0) return null;

    // Find the rule that's currently being validated (the one after the last completed)
    const currentIndex = streamingProgress.current;
    if (currentIndex < sortedUserRules.length) {
      const userRule = sortedUserRules[currentIndex];
      const libraryRule = libraryRules.find(lr =>
        lr.id?.toString() === userRule.libraryRuleId?.toString()
      );
      return libraryRule?.name || userRule?.name || null;
    }
    return null;
  }, [validating, streamingProgress, sortedUserRules, libraryRules]);

  const toggleGate = (ruleId) => {
    setExpandedGates((prev) => ({
      ...prev,
      [ruleId]: !prev[ruleId],
    }));
  };

  const handleConfigure = (userRule, libraryRule) => {
    openConfigModal(userRule, libraryRule);
  };

  // Count stats from user rules
  const enabledRules = sortedUserRules.filter(r => r.enabled !== false);
  const passedCount = enabledRules.filter(r => {
    // Check by userRuleId first, then libraryRuleId
    let result = validationMap.get(r.id?.toString());
    if (!result) {
      result = validationMap.get(r.libraryRuleId?.toString());
    }
    return result?.status === 'PASSED' || result?.status === GateStatus.PASSED;
  }).length;
  const failedCount = enabledRules.filter(r => {
    // Check by userRuleId first, then libraryRuleId
    let result = validationMap.get(r.id?.toString());
    if (!result) {
      result = validationMap.get(r.libraryRuleId?.toString());
    }
    return result?.status === 'FAILED' || result?.status === GateStatus.FAILED;
  }).length;
  const totalRules = enabledRules.length;

  // Summary text
  const getSummaryText = () => {
    if (totalRules === 0) {
      return <span className="summary-empty">No rules</span>;
    }
    if (failedCount > 0) {
      return <span className="summary-failed">{failedCount} failed</span>;
    }
    if (passedCount > 0 && passedCount === totalRules) {
      return <span className="summary-passed">All passed</span>;
    }
    if (passedCount > 0) {
      return <span className="summary-passed">{passedCount}/{totalRules} passed</span>;
    }
    return <span className="summary-pending">{totalRules} rules</span>;
  };

  // Determine if this is a re-run (has previous results)
  const hasResults = gates && gates.length > 0;
  const canValidate = hasTrade && totalRules > 0 && !validating && canValidateByLimit;

  // Get status display
  // This function is replaced by the ValidationStatusBanner component
  // const getStatusBanner = () => {
  //   if (!hasResults) return null;
  //   ...
  // };

  const toggleCollapse = () => {
    if (onToggle) {
      // Controlled mode (accordion)
      onToggle();
    } else {
      // Internal state mode (backward compatibility)
      setInternalCollapsed(!internalCollapsed);
    }
  };

  // Compact status display for collapsed view
  const getCompactStatus = () => {
    const isGo = status === 'go' || status === 'GO';
    const isNoGo = status === 'no_go' || status === 'NO_GO';

    if (isGo) {
      return (
        <span className="compact-status compact-go">
          <CheckCircleIcon fontSize="small" />
          PASSED
        </span>
      );
    }
    if (isNoGo) {
      return (
        <span className="compact-status compact-no-go">
          <CancelIcon fontSize="small" />
          MISMATCH
        </span>
      );
    }
    return null;
  };


  return (
    <div className={`conceptgate-section conceptgate-pipeline ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Collapsed View - Always rendered, shown when collapsed */}
      <div className={`pipeline-collapsed-view ${isCollapsed ? '' : 'hidden'}`}>
        <div className="pipeline-collapsed-content" onClick={toggleCollapse}>
          <div className="pipeline-collapsed-header">
            <FactCheckIcon fontSize="small" />
            <span className="pipeline-title">Validation</span>
            {activePipeline?.name && (
              <span className="pipeline-name-badge">{activePipeline.name}</span>
            )}
            {getCompactStatus()}
            {totalRules > 0 && (
              <span className="pipeline-total-rules">{totalRules} {totalRules === 1 ? 'rule' : 'rules'}</span>
            )}
          </div>
            <div className="pipeline-compact-stats">
              {hasResults ? (
                <>
                  {passedCount > 0 && (
                    <span className="pipeline-stat">
                      <span className="stat-label">Passed</span>
                      <span className="stat-value passed">{passedCount}</span>
                    </span>
                  )}
                  {failedCount > 0 && (
                    <span className="pipeline-stat">
                      <span className="stat-label">Failed</span>
                      <span className="stat-value failed">{failedCount}</span>
                    </span>
                  )}
                </>
              ) : totalRules > 0 ? (
                <span className="pipeline-stat">
                  <span className="stat-label">Status</span>
                  <span className="stat-value pending">Ready</span>
                </span>
              ) : (
                <span className="pipeline-stat">
                  <span className="stat-label">Status</span>
                  <span className="stat-value empty">No rules</span>
                </span>
              )}
            {totalRules > 0 && (
              <div className="pipeline-collapsed-action">
                <button
                  className={`pipeline-validate-btn compact ${validating ? 'validating' : ''} ${limitReached ? 'limit-reached' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onValidate(); }}
                  disabled={!hasTrade || validating || limitReached || isCodexUi}
                  title={limitReached ? `Daily limit reached.${formatResetTime(subscriptionStatus?.resetAt)} ` : ''}
                >
                  {validating ? (
                    <>
                      <CircularProgress size={14} color="inherit" />
                      <span>
                        {streamingProgress.total > 0
                          ? `${streamingProgress.current}/${streamingProgress.total}`
                          : '...'}
                      </span>
                    </>
                  ) : limitReached ? (
                    <span>{formatResetTime(subscriptionStatus?.resetAt) || 'Limit Reached'}</span>
                  ) : hasResults ? (
                    <>
                      <ReplayIcon fontSize="small" />
                      <span>Run</span>
                    </>
                  ) : (
                    <>
                      <PlayArrowIcon fontSize="small" />
                      <span>Validate</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
          <span className="collapse-toggle">
            <ExpandMoreIcon fontSize="small" />
          </span>
        </div>
      </div>

      {/* Expanded View - Always rendered, shown when expanded */}
      <div className={`expanded-view ${isCollapsed ? 'hidden' : ''}`}>
        {/* Status Banner */}
        {hasResults && (
          <ValidationStatusBanner
            status={status}
            validationResult={validationResult}
            isAdmin={isAdmin}
            onSave={onSaveToJournal}
            isSaved={isJournalSaved}
          />
        )}

        <div className="conceptgate-section-header" onClick={toggleCollapse}>
            <FactCheckIcon fontSize="small" />
            <span>Validation Pipeline</span>
            <span className="pipeline-summary">
              {getSummaryText()}
            </span>
            <span className="collapse-toggle" title="Collapse">
              <ExpandLessIcon fontSize="small" />
            </span>
            <button
              className="pipeline-edit-btn"
              onClick={(e) => { e.stopPropagation(); onOpenRulesManagement(); }}
              title="Add rules"
            >
              <AddIcon fontSize="small" />
            </button>
          </div>

        {/* Pipeline Selector */}
        <div className="conceptgate-section-content">
          <PipelineSelector subscriptionStatus={subscriptionStatus} />
        </div>

        {/* Expanded Content */}
        {/* Background Validation Message */}
        {(() => {
          const normalizedInstrument = selectedInstrument?.replace('_', '').toUpperCase();
          const backgroundValidation = normalizedInstrument && backgroundValidationMap?.[normalizedInstrument];
          if (backgroundValidation) {
            const elapsedSeconds = Math.floor((Date.now() - backgroundValidation.startTime) / 1000);
            return (
              <div className="background-validation-banner" style={{
                padding: '8px 12px',
                margin: '8px 0',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                fontSize: '13px',
                color: '#856404',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <CircularProgress size={14} />
                <span>
                  Connection lost, but validation is continuing in the background. 
                  Results will appear automatically when ready. ({elapsedSeconds}s)
                </span>
              </div>
            );
          }
          return null;
        })()}
        {/* Run/Re-run Button */}
            {totalRules > 0 && (
              <div className="pipeline-action-bar">
                <div className="pipeline-action-buttons">
                  <button
                    className={`pipeline-validate-btn ${validating ? 'validating' : ''} ${limitReached ? 'limit-reached' : ''}`}
                    onClick={onValidate}
                    disabled={validating || !canValidate || isCodexUi}
                    title={validating ? 'Validation in progress...' : limitReached ? `Daily limit reached. ${formatResetTime(subscriptionStatus?.resetAt)}` : hasResults ? 'Re-run validation (market conditions may have changed)' : 'Run validation'}
                  >
                    {validating ? (
                      <>
                        <CircularProgress size={14} color="inherit" />
                        <span>
                          {streamingProgress.total > 0
                            ? `${streamingProgress.current}/${streamingProgress.total}`
                            : 'Starting...'}
                        </span>
                        {estimatedTimeRemaining && estimatedTimeRemaining > 0 && (
                          <span className="time-estimate">~{estimatedTimeRemaining}s</span>
                        )}
                      </>
                    ) : limitReached ? (
                      <>
                        <span>{formatResetTime(subscriptionStatus?.resetAt) || 'Limit Reached'}</span>
                      </>
                    ) : hasResults ? (
                      <>
                        <ReplayIcon fontSize="small" />
                        <span>Re-validate</span>
                        {remainingText && <span className="remaining-badge">{remainingText}</span>}
                      </>
                    ) : (
                      <>
                        <PlayArrowIcon fontSize="small" />
                        <span>Validate</span>
                        {remainingText && <span className="remaining-badge">{remainingText}</span>}
                      </>
                    )}
                  </button>
                  {validating && onCancelValidation && (
                    <button
                      className="pipeline-cancel-btn"
                      onClick={onCancelValidation}
                      title="Cancel validation"
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  )}
                </div>
                {validating && currentValidatingRule ? (
                  <div className="pipeline-nudge-text">
                    {getNudgeMessage(currentValidatingRule)}
                  </div>
                ) : (
                  <div className="pipeline-disclaimer-text">
                    Checking against your personally configured rules
                  </div>
                )}
              </div>
            )}

            <div className="conceptgate-section-content">
              {isLoading ? (
                <div className="pipeline-skeleton-list">
                  <SkeletonRuleItem />
                  <SkeletonRuleItem />
                  <SkeletonRuleItem />
                </div>
              ) : sortedUserRules.length === 0 ? (
                <div className="pipeline-empty">
                  <PlaylistAddCheckIcon className="empty-icon" />
                  <p className="empty-title">No validation rules yet</p>
                  <p className="empty-description">
                    Add rules to validate your trade ideas before execution
                  </p>
                  <button className="add-rules-btn" onClick={onOpenRulesManagement}>
                    <AddIcon fontSize="small" />
                    Add Rules
                  </button>
                </div>
              ) : (
                <div className="conceptgate-gate-list">
                  {sortedUserRules.map((userRule, index) => {
                    const libraryRule = libraryRules.find((lr) => {
                      const lrId = lr.id?.toString();
                      const urId = userRule.libraryRuleId?.toString();
                      return lrId === urId;
                    });

                    // Match validation result
                    // Priority:
                    // 1. userRuleId (Unique instance)
                    // 2. libraryRuleId (Legacy/shared)
                    // 3. ruleName (Last resort)
                    let validationResultForRule = validationMap.get(userRule.id?.toString());

                    if (!validationResultForRule) {
                      validationResultForRule = validationMap.get(userRule.libraryRuleId?.toString());
                    }
                    if (!validationResultForRule && libraryRule?.name) {
                      validationResultForRule = validationMap.get(libraryRule.name);
                    }

                    const ruleKey = userRule.id || userRule.libraryRuleId;

                    // Determine if this rule is currently being validated
                    // It's validating if: we're validating AND this rule's index matches the next to be validated
                    // AND there's no result for this rule yet
                    const isCurrentlyValidating = validating &&
                      userRule.enabled !== false &&
                      streamingProgress.total > 0 &&
                      !validationResultForRule &&
                      index === streamingProgress.current;

                    return (
                      <RuleItem
                        key={ruleKey}
                        userRule={userRule}
                        libraryRule={libraryRule}
                        validationResult={validationResultForRule}
                        isExpanded={expandedGates[ruleKey]}
                        onToggle={() => toggleGate(ruleKey)}
                        onConfigure={() => handleConfigure(userRule, libraryRule)}
                        onRemove={() => handleRemoveRule(userRule, libraryRule)}
                        onToggleEnabled={() => handleToggleEnabled(userRule)}
                        isCurrentlyValidating={isCurrentlyValidating}
                        // Drag and drop props
                        draggable={!validating && !isReordering && !!activePipeline?.id}
                        onDragStart={(e) => handleDragStart(e, userRule.id)}
                        onDragOver={(e) => handleDragOver(e, userRule.id)}
                        onDragLeave={handleDragLeave}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, userRule.id)}
                        isDragging={draggedRuleId === String(userRule.id)}
                        isDragOver={dragOverRuleId === String(userRule.id)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
      </div>

      {/* Undo Toast Stack - shows all pending deletions */}
      {
        undoQueue && undoQueue.length > 0 && (
          <div className="undo-toast-stack">
            {undoQueue.map((undoItem, index) => (
              <div
                key={undoItem.ruleId}
                className="undo-toast"
                style={{ transform: `translateY(${-index * 8}px)`, zIndex: 100 - index }}
              >
                <span className="undo-toast-message">{undoItem.message}</span>
                <button className="undo-toast-btn" onClick={() => undoRuleRemovalById(undoItem.ruleId)}>
                  <UndoIcon fontSize="small" />
                  Undo
                </button>
                <button className="undo-toast-close" onClick={() => dismissUndoItem(undoItem.ruleId)}>
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            ))}
          </div>
        )
      }
    </div>
  );
};

export default ValidationPipeline;
